export type CancelOutcome = 'cancelled' | 'gone' | 'finished' | 'forbidden' | 'error';

export type CancelAction = 'clearPersistedKey' | 'clearStore' | 'refreshStatus';

// Map a cancel response status code to a UI outcome.
// 204 = success (run cancelled), 404 = already gone, 409 = not cancellable
// (already finished), 403 = window closed / not authorised, anything else = generic error.
export const classifyCancelOutcome = (statusCode: number | undefined): CancelOutcome => {
  switch (statusCode) {
    case 204: return 'cancelled';
    case 404: return 'gone';
    case 409: return 'finished';
    case 403: return 'forbidden';
    default: return 'error';
  }
};

// Read the statusCode off a rejected StatusResponse (mirrors classifyLoadError).
export const cancelStatusCode = (err: unknown): number | undefined =>
  (err as { statusCode?: number } | null | undefined)?.statusCode;

const cancelOutcomeCopy: Record<CancelOutcome, string | undefined> = {
  cancelled: undefined,
  gone: 'This run no longer exists.',
  finished: 'This run already finished.',
  forbidden: "You don't have permission to cancel this run.",
  error: "Couldn't cancel the run. Try again.",
};

export const cancelOutcomeMessage = (outcome: CancelOutcome): string | undefined => cancelOutcomeCopy[outcome];

// Ordered UI actions per outcome. The order for 'gone' is load-bearing: the
// persisted session key MUST be cleared BEFORE the store, otherwise the
// use-ai-scrub-progress effect re-runs on the cleared activeBatchRunId, re-reads
// the still-present sessionStorage key, and resurrects the dead run id.
export const cancelOutcomeActions = (outcome: CancelOutcome): CancelAction[] => {
  switch (outcome) {
    case 'gone': return ['clearPersistedKey', 'clearStore'];
    case 'finished': return ['refreshStatus'];
    default: return [];
  }
};
